const __slice = Array.prototype.slice;

class Rectangle {
  /**
   * Given an aspect ratio, calculates the biggest rectangle that fits within
   * @param  {Number} ratio numeric aspect ratio for the output (e.g. 6:4 is 1.5)
   * @param  {Number} iWidth width of the input rectangle (e.g. photo width)
   * @param  {Number} iHeight height of the input image (e.g. photo height)
   * @return {Array[Number]} co-ordinates of cropped area within the input
   */
  static fitLargestRectangle(ratio, iWidth, iHeight) {
    let ch;
    let cw;
    let x;
    let y;
    const rectRatio = iHeight / iWidth;

    if (ratio > rectRatio) {
      ch = iHeight;
      cw = iHeight / ratio;
      y = 0;
      x = (iWidth - cw) / 2;
    } else {
      const { features } = window;

      cw = iWidth;
      ch = iWidth * ratio;
      x = 0;
      y = features?.printsBuilderSetDefaultCropTopAligned
        ? 0
        : (iHeight - ch) / 2;
    }

    return [x, y, cw, ch];
  }

  static auto() {
    let args;
    args = arguments.length >= 1 ? __slice.call(arguments, 0) : [];
    if (args.length === 4) {
      return new Rectangle(args[0], args[1], args[2], args[3]);
    }
    if (args[0] instanceof Rectangle) {
      return args[0];
    }
    if (args.length === 1 && args[0] instanceof Array) {
      args = args[0];
      return new Rectangle(args[0], args[1], args[2], args[3]);
    }
  }

  constructor(x, y, width, height) {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
  }

  containsPoint({ x, y }) {
    return (
      x >= this.x &&
      x <= this.x + this.width &&
      y >= this.y &&
      y <= this.y + this.height
    );
  }

  ratio(rect) {
    if (rect instanceof Array) {
      return rect[3] / rect[2];
    }
    if (rect.w && rect.h) {
      return rect.h / rect.w;
    }
    if (rect.width && rect.height) {
      return rect.height / rect.width;
    }
  }

  rectIsRatio(rect, ratio) {
    return Math.equals(this.ratio(rect), ratio);
  }

  rectMatchesRatio(rect, ratio) {
    const r_ratio = this.ratio(rect);
    return (
      Math.abs(r_ratio - ratio) < 0.01 || Math.abs(r_ratio - 1.0 / ratio) < 0.01
    );
  }

  findLargestRectangle(ratio) {
    let ch;
    let cw;
    let x;
    let y;
    const rectRatio = this.height / this.width;

    if (ratio > rectRatio) {
      ch = this.height;
      cw = this.height / ratio;
      y = 0;
      x = (this.width - cw) / 2;
    } else {
      cw = this.width;
      ch = this.width * ratio;
      x = 0;
      y = (this.height - ch) / 2;
    }
    return [x, y, cw, ch];
  }

  shrinkToFit(printRatio, bestFit) {
    let altPrintRatio;
    let imgRatio;
    let out;
    if (bestFit === null) {
      bestFit = true;
    }
    imgRatio = this.height / this.width;
    altPrintRatio = 1 / printRatio;
    if (bestFit) {
      if (
        Math.abs(altPrintRatio - imgRatio) < Math.abs(printRatio - imgRatio)
      ) {
        printRatio = altPrintRatio;
      }
    }
    if (imgRatio > printRatio) {
      out = {
        height: this.height,
        width: Math.ceil(this.height / printRatio),
      };
    } else {
      out = {
        width: this.width,
        height: Math.ceil(this.width * printRatio),
      };
    }
    out.x = Math.ceil((out.width - this.width) / 2);
    out.y = Math.ceil((out.height - this.height) / 2);
    return [-out.x, -out.y, out.width, out.height];
  }

  modifyLimits = qtty => {
    this.x -= qtty;
    this.y -= qtty;
    this.height += qtty * 2;
    this.width += qtty * 2;
  };
}

export default Rectangle;
